<template>
  <div class="choose-layout">
    <div class="choose-page">
      <div class="choose-page__logo">
        <img
          src="@/assets/images/logo-tc-black.svg"
          alt="ShopiMind"
        >
      </div>

      <div class="choose-card">
        <div
          v-if="isLoading"
          class="choose-card__loader"
        >
          <i class="pi pi-spin pi-spinner" />
        </div>

        <div class="choose-card__header">
          <h1 class="choose-card__title">
            {{ t('chooseShops.title') }}
          </h1>
          <p class="choose-card__desc">
            {{ t('chooseShops.message') }}
          </p>
        </div>

        <div
          v-if="nbTotalShops > 10"
          class="choose-search"
        >
          <i class="fa-regular fa-magnifying-glass choose-search__icon" />
          <InputText
            v-model="searchValue"
            class="choose-search__input"
            :placeholder="t('search')"
          />
        </div>

        <div class="choose-list">
          <div
            v-for="shop in displayedShops"
            :key="shop.id"
            class="choose-item"
            @click="selectShop(shop)"
          >
            <div class="choose-item__logo">
              <img
                v-if="shop.logo"
                :src="`https://media.shopimind.io/clients/logos/${shop.logo}`"
                :alt="shop.name || shop.url"
                @error="onImgError($event)"
              >
              <i
                v-else
                class="fa-solid fa-store"
              />
            </div>
            <div class="choose-item__info">
              <span class="choose-item__name">{{ shop.name || shop.url }}</span>
              <span class="choose-item__url">{{ shop.url }}</span>
            </div>
            <i class="fa-solid fa-chevron-right choose-item__arrow" />
          </div>
        </div>

        <div class="choose-card__foot">
          <button
            class="choose-logout"
            @click="handleLogout"
          >
            <i class="fa-solid fa-right-from-bracket" />
            {{ t('logout') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent, onBeforeMount, ref, Ref,
} from 'vue';
import { useI18n } from 'vue-i18n';

import {
  getIsAuthenticated, Logout, Me, refreshTokenLogin, Shop, UserState,
} from '@/composables/User';
import { useRouter } from 'vue-router';
import InputText from 'primevue/inputtext';
import { useStore } from '@/store';
import { showToastError } from '@/helpers';

export default defineComponent({
  name: 'ChooseShop',

  components: {
    InputText,
  },

  setup() {
    const { t } = useI18n();
    const router = useRouter();
    const store = useStore();
    const isLoading = ref(false);
    const searchValue = ref('');

    const nbTotalShops = computed(() => UserState.user.shops.length);

    const displayedShops = computed(() => {
      const regex = new RegExp(searchValue.value, 'i');
      return UserState.user.shops
        .filter((s: Shop) => (regex.test(s.name) || regex.test(s.id.toString()) || regex.test(s.url)))
        .slice(0, 10);
    });

    const selectShop = async (shop: Shop) => {
      isLoading.value = true;

      try {
        const res: any = await refreshTokenLogin(shop.id);
        if (res && res.success) {
          const { idShop } = res;
          await Me(true, Number(idShop));
          store.commit('liveEditor/resetWebsiteContents');
          await router.push({ name: 'home' });
        } else {
          await showToastError('GENERIC_ERROR');
        }
      } catch (e) {
        await showToastError('GENERIC_ERROR');
      }

      isLoading.value = false;
    };

    const handleLogout = async () => {
      try {
        await Logout();
      } catch (e) {
        // ignore
      }
      await router.push({ name: 'login' });
      window.location.reload();
    };

    const onImgError = (event: Event) => {
      const img = event.target as HTMLImageElement;
      img.style.display = 'none';
      const parent = img.parentElement;
      if (parent && !parent.querySelector('i')) {
        const icon = document.createElement('i');
        icon.className = 'fa-solid fa-store';
        parent.appendChild(icon);
      }
    };

    onBeforeMount(async () => {
      const isAuthenticated = getIsAuthenticated();
      if (!isAuthenticated) {
        await Me(false);
      }
      return true;
    });

    return {
      t,
      nbTotalShops,
      displayedShops,
      searchValue,
      isLoading,
      selectShop,
      handleLogout,
      onImgError,
    };
  },
});
</script>

<style scoped lang="scss">
.choose-layout {
  min-height: 100vh;
  background: #f7f8fa;
  display: flex;
  align-items: center;
  justify-content: center;
}

.choose-page {
  width: 100%;
  max-width: 500px;
  padding: 2rem;
}

.choose-page__logo {
  margin-bottom: 1.5rem;
  text-align: center;
  img { height: 36px; width: auto; }
}

/* ── Card ── */
.choose-card {
  position: relative;
  background: #fff;
  border-radius: 16px;
  padding: 2rem 2.25rem;
  box-shadow: 0 1px 3px rgba(0,0,0,0.03), 0 8px 30px rgba(0,0,0,0.06);
}

.choose-card__loader {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;

  i { font-size: 1.5rem; color: #94c840; }
}

.choose-card__header {
  margin-bottom: 1.5rem;
}

.choose-card__title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 0.25rem;
  letter-spacing: -0.02em;
}

.choose-card__desc {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0;
  line-height: 1.5;
}

/* ── Search ── */
.choose-search {
  position: relative;
  margin-bottom: 1rem;
}

.choose-search__icon {
  position: absolute;
  left: 0.875rem;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.8125rem;
  color: #9ca3af;
  z-index: 1;
}

.choose-search__input {
  width: 100%;
  padding-left: 2.25rem !important;
  border-radius: 10px !important;
  border-color: #dfe3e8 !important;
  font-size: 0.875rem;
  transition: border-color 0.15s, box-shadow 0.15s;

  &:hover { border-color: #c4cdd5 !important; }
  &:focus {
    border-color: #94c840 !important;
    box-shadow: 0 0 0 3px rgba(148, 200, 64, 0.12) !important;
  }
}

/* ── Shop list ── */
.choose-list {
  max-height: 320px;
  overflow-y: auto;
  margin: 0 -0.5rem;
  padding: 0 0.5rem;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #e5e7eb;
    border-radius: 4px;
  }
}

.choose-item {
  display: flex;
  align-items: center;
  gap: 0.875rem;
  padding: 0.75rem;
  border-radius: 10px;
  cursor: pointer;
  transition: background-color 0.15s;

  &:hover {
    background: #f3f4f6;

    .choose-item__arrow {
      opacity: 1;
      color: #94c840;
    }
  }

  & + & {
    border-top: 1px solid #f3f4f6;
  }

  &:hover + &,
  & + &:hover {
    border-top-color: transparent;
  }
}

.choose-item__logo {
  width: 40px;
  height: 40px;
  min-width: 40px;
  border-radius: 10px;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    padding: 4px;
  }

  i {
    font-size: 1rem;
    color: #9ca3af;
  }
}

.choose-item__info {
  flex: 1;
  min-width: 0;
}

.choose-item__name {
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.choose-item__url {
  display: block;
  font-size: 0.75rem;
  color: #9ca3af;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.choose-item__arrow {
  font-size: 0.6875rem;
  color: #d1d5db;
  opacity: 0;
  transition: opacity 0.15s, color 0.15s;
}

/* ── Footer ── */
.choose-card__foot {
  margin-top: 1.25rem;
  padding-top: 1rem;
  border-top: 1px solid #f3f4f6;
  text-align: center;
}

.choose-logout {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 0.8125rem;
  font-weight: 500;
  color: #9ca3af;
  transition: color 0.15s;
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;

  &:hover {
    color: #ef4444;
  }
}
</style>
